import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { pushEvent } from '../../utils/analytics';
import { fetchCatalog } from '../../api/checkout';
import { fetchBootcampOfferSummary, formatBootcampOfferDate } from './challengePageUtils';
import './ChallengeBookingPage.css';

const BOOTCAMP_PATH = '/courses/ai-trading-bootcamp';

export default function ChallengeCallConfirmedPage() {
  const { slug } = useParams();
  const [bootcampOffer, setBootcampOffer] = useState(null);
  const [tuitionWindow, setTuitionWindow] = useState(null);
  const [offerLoaded, setOfferLoaded] = useState(false);

  useEffect(() => {
    pushEvent({
      event: 'expert_call_booking_success_page_viewed',
      source: window.location.pathname,
      referrer: document.referrer || 'calendly_embed',
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadOffer = async () => {
      try {
        const summary = await fetchBootcampOfferSummary(fetchCatalog);
        if (!mounted) return;
        setBootcampOffer({
          name: summary.name,
          cohortName: summary.cohortName,
          startsAt: summary.startsAt,
        });
        setTuitionWindow(summary.tuitionWindow);
      } catch {
        if (!mounted) return;
        setBootcampOffer(null);
        setTuitionWindow(null);
      } finally {
        if (mounted) setOfferLoaded(true);
      }
    };
    loadOffer();
    return () => {
      mounted = false;
    };
  }, []);

  const nextCohortDate = useMemo(
    () => formatBootcampOfferDate(bootcampOffer?.startsAt),
    [bootcampOffer?.startsAt],
  );
  const enrollmentEnds = useMemo(
    () => formatBootcampOfferDate(tuitionWindow?.endDate),
    [tuitionWindow?.endDate],
  );

  return (
    <main
      className="challenge-next challenge-next--confirmed"
      data-testid="challenge-call-confirmed-page"
    >
      <Helmet>
        <title>Call booked | BotSpot</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <section className="challenge-next__confirmed-card">
        <div className="challenge-next__check" aria-hidden="true">
          ✓
        </div>
        <p className="challenge-next__eyebrow">YOU ARE SET</p>
        <h1>You're booked. Talk soon.</h1>
        <p>Check your email for the calendar invite and meeting link.</p>
      </section>
      <section className="challenge-next__offer" aria-labelledby="challenge-offer-title">
        <p className="challenge-next__eyebrow">THE FULL PROGRAM</p>
        <h2 id="challenge-offer-title">
          The challenge is 7 days. The Bootcamp is the whole thing.
        </h2>
        {offerLoaded ? (
          <>
            <p className="challenge-next__offer-name">
              {bootcampOffer?.name || 'The live AI Trading Bootcamp'}
            </p>
            <p>
              The challenge shows you the tools. The Bootcamp is where you build your own system
              with an instructor watching your work every week, not just this one.
            </p>
            <div className="challenge-next__offer-facts" aria-label="Current Bootcamp details">
              {nextCohortDate ? (
                <strong>
                  {bootcampOffer?.cohortName || 'The next cohort'} starts {nextCohortDate}.
                </strong>
              ) : null}
              {enrollmentEnds ? (
                <span>
                  {tuitionWindow.label || 'Current enrollment'} ends {enrollmentEnds}.
                </span>
              ) : null}
            </div>
          </>
        ) : (
          <p className="challenge-next__status">Loading the next live Bootcamp...</p>
        )}
        <div className="challenge-next__actions">
          <Link
            className="challenge-next__primary"
            to={`${BOOTCAMP_PATH}?utm_source=challenge_call_confirmed&utm_medium=booking&utm_campaign=ai_trading_challenge`}
          >
            Explore the live Bootcamp
          </Link>
        </div>
      </section>
      <Link className="challenge-next__skip" to={`/challenges/${slug}`}>
        Back to the challenge
      </Link>
    </main>
  );
}
